import React, { createContext, useState, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import _ from 'lodash';

import { data_from_server_get, data_from_server_post, download_file_with_progress } from '@/shared/http/client';
import {
    downloadPreparedModelArtifact,
    requestSaveTarget,
    PickerUnavailableError,
} from '@/shared/utils/downloadFromSignedUrl';
import { deployed, ready_to_deploy, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { sparkToast } from '@spark';
import { getOrCreateClientUUID } from '@/shared/utils/helpers';
import {
    appendPendingUploadCleanup,
    removePendingUploadCleanup,
} from '@/features/datasets/utils/pendingUploadCleanupStorage';
import { TRAINING_STATUSES, FINAL_TRAINING_STATUSES } from '@/features/trainings/constants';
import { mergeCoalescedGroupChildIntoParent, findLabelGenerationProgressKey, foldGenerationProgressIntoUploadItem } from '@/features/datasets/utils/uploadProgress';
import { mergeTeacherEvaluationStatus } from '@/features/evaluation/utils/mergeTeacherEvaluationStatus';
import {
    loadComposeUploadGroups,
    removeComposeUploadGroup,
    upsertComposeUploadGroup,
} from '@/features/datasets/utils/uploadGroupSession';
import { mapEcrOverallProgress } from '@/shared/utils/downloadProgressUtils';
import { buildLabelGenWarningMessage } from '@/features/datasets/utils/labelGenerationWarning';
import { applyImproveRetrainGenerationTerminal } from '@/features/trainings/utils/improveRetrainPending';
import {
    applyAutoTrainFlowTerminal,
    hasSucceededAutoTrainFlow,
} from '@/features/trainings/utils/autoTrainPending';
import { resolveAutoTrainStatusUpdate } from '@/features/trainings/utils/autoTrainStatusWs';
import { listActiveDataFlowsForUsecase } from '@/features/trainings/utils/autoTrain';
import {
    applyMonotonicGenerationProgress,
    buildGenerationSummaryWarningFields,
    generationProgressBarItem,
    generationProgressItemsFromStatus,
    generationSummaryExtra,
    inProgressStepPercent,
    isTrajectoryGenerationType,
    mergeGenerationProgressIntoStatus,
    normalizeGenerationProgressPayload,
    seedGenerationProgressItem,
    buildCancelGenerationRequest,
} from '@/features/datasets/utils/generationProgressStatus';
import { aiGenerationProgressKey } from '@/features/datasets/utils/datasetOverviewProgress';
import {
    findRetrievalAgentProgressKeyForDataset,
    foldDatasetProgressIntoRetrievalAgent,
    markRetrievalAgentTrajectoryGeneration,
    mergeRetrievalAgentProgressIntoStatus,
    retrievalAgentBarItem,
    retrievalAgentItemsFromStatus,
    seedRetrievalAgentProgressItem,
} from '@/features/datasets/utils/retrievalAgentProgress';

// localStorage cache for the commercial-model catalog. Hydrating
// synchronously from this key lets the model picker render with the
// correct provider segment / dropdown on the very first paint, before
// ``GET /available-models`` resolves. The cache is overwritten by every
// successful fetch and explicitly cleared on logout (see AuthContext).
const COMMERCIAL_MODELS_CACHE_KEY = 'specificai:availableProviders:v1';

// While a Retrieval Agent session or generation job sits in the broker queue,
// its queue position only changes when another job starts or finishes, so a
// slow poll keeps the "N jobs ahead" subtitle fresh without hammering the API.
const QUEUED_STATUS_POLL_INTERVAL_MS = 10_000;

const readCachedProviders = () => {
    try {
        const raw = window.localStorage.getItem(COMMERCIAL_MODELS_CACHE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // Defensive: only accept the documented shape (a plain object map)
        // so a corrupted or stale-format entry can't poison consumers.
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
        }
        return {};
    } catch {
        return {};
    }
};

const writeCachedProviders = (providers) => {
    try {
        window.localStorage.setItem(
            COMMERCIAL_MODELS_CACHE_KEY,
            JSON.stringify(providers || {})
        );
    } catch {
        // Storage may be unavailable (private mode, quota); the cache is
        // a best-effort optimization and the in-memory state still
        // reflects the latest fetch.
    }
};

export const clearCachedAvailableProviders = () => {
    try {
        window.localStorage.removeItem(COMMERCIAL_MODELS_CACHE_KEY);
    } catch {
        // See note in writeCachedProviders.
    }
};

function findDownloadProgressKey(prev, usecaseId, version, downloadId, artifactType) {
    if (downloadId && prev[downloadId]) {
        return downloadId;
    }
    return Object.keys(prev).find((key) => {
        const item = prev[key];
        if (!item.isDownload || item.usecaseId !== usecaseId || item.version !== version) {
            return false;
        }
        // A raw-files (zip) download and a Docker-image download can be in
        // flight for the same model + version at once. When the caller knows
        // which artifact this event belongs to, only match a row of the same
        // artifact type so the two never collapse onto a single progress item.
        if (artifactType && item.artifactType && item.artifactType !== artifactType) {
            return false;
        }
        return true;
    });
}

function resolveDownloadProgressKey(prev, event, fallbackDownloadId) {
    if (event.downloadId && prev[event.downloadId]) {
        return event.downloadId;
    }
    const byUsecase = findDownloadProgressKey(
        prev,
        event.usecaseId,
        event.version,
        event.downloadId,
        event.artifactType
    );
    return byUsecase || event.downloadId || fallbackDownloadId;
}

async function startModelArtifactDownload({
    event,
    fileExtension,
    downloadId,
    setProgressStatus,
    savedHandle,
}) {
    const artifactType = event.artifactType || 'zip';
    const filename = event.filename?.endsWith(fileExtension)
        ? event.filename
        : `${event.filename}${fileExtension}`;
    const totalBytes = event.sizeBytes;

    setProgressStatus((prev) => {
        const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
        const targetKey = existingKey || downloadId;
        const existingItem = existingKey ? prev[existingKey] : null;
        return {
            ...prev,
            [targetKey]: {
                ...existingItem,
                phase: 'downloading',
                buildProgress: 100,
                downloadProgress: 0,
                downloadIndeterminate: false,
                isCompleted: false,
                isError: false,
                progress: 100,
                message: 'Downloading to your computer…',
                downloadPath: event.downloadPath,
                filename: event.filename,
                isDownload: true,
                downloadId: targetKey,
                usecaseId: event.usecaseId,
                version: event.version,
                clientId: event.client_id,
                datasetName: existingItem?.datasetName || `Model v${event.version}`,
                artifactType: event.artifactType || existingItem?.artifactType || 'zip',
                sizeBytes: totalBytes ?? existingItem?.sizeBytes,
            },
        };
    });

    try {
        const result = await downloadPreparedModelArtifact(
            {
                client_id: event.client_id || '40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75',
                llm_usecase_id: event.usecaseId,
                version: event.version,
                download_path: event.downloadPath,
                filename: event.filename,
                artifact_type: artifactType,
            },
            filename,
            {
            writable: savedHandle,
            totalBytes,
            onProgress: (downloadProgress) => {
                setProgressStatus((prev) => {
                    const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
                    const targetKey = existingKey || downloadId;
                    const existingItem = prev[targetKey] || {};
                    return {
                        ...prev,
                        [targetKey]: {
                            ...existingItem,
                            phase: 'downloading',
                            buildProgress: 100,
                            downloadProgress,
                            downloadIndeterminate: false,
                            isCompleted: false,
                            progress: downloadProgress,
                        },
                    };
                });
            },
        }
        );

        if (result.deliveryMode === 'local_path') {
            setProgressStatus((prev) => {
                const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
                const targetKey = existingKey || downloadId;
                return {
                    ...prev,
                    [targetKey]: {
                        ...prev[targetKey],
                        phase: 'complete',
                        buildProgress: 100,
                        downloadProgress: 100,
                        progress: 100,
                        isCompleted: true,
                        message: 'File saved on disk – click to copy path',
                        localFilePath: result.localFilePath,
                        sizeBytes: result.sizeBytes ?? prev[targetKey]?.sizeBytes,
                    },
                };
            });
            return;
        }

        if (result.cancelled) {
            setProgressStatus((prev) => {
                const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
                const targetKey = existingKey || downloadId;
                const existingItem = prev[targetKey] || {};
                const next = { ...prev };
                delete next[targetKey];
                return next;
            });
            return;
        }

        if (!result.success) {
            throw new Error(result.message || 'Download failed');
        }

        if (result.indeterminate) {
            setProgressStatus((prev) => {
                const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
                const targetKey = existingKey || downloadId;
                const existingItem = prev[targetKey] || {};
                return {
                    ...prev,
                    [targetKey]: {
                        ...existingItem,
                        phase: 'complete',
                        buildProgress: 100,
                        downloadProgress: 100,
                        downloadIndeterminate: false,
                        isCompleted: true,
                        progress: 100,
                        message: 'Downloading via browser – check the toolbar for progress',
                    },
                };
            });
            return;
        }

        setProgressStatus((prev) => {
            const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
            const targetKey = existingKey || downloadId;
            const existingItem = prev[targetKey] || {};
            return {
                ...prev,
                [targetKey]: {
                    ...existingItem,
                    phase: 'complete',
                    buildProgress: 100,
                    downloadProgress: 100,
                    downloadIndeterminate: false,
                    isCompleted: true,
                    progress: 100,
                    message: 'Download complete',
                },
            };
        });
    } catch (err) {
        setProgressStatus((prev) => {
            const existingKey = findDownloadProgressKey(prev, event.usecaseId, event.version, downloadId, event.artifactType);
            const targetKey = existingKey || downloadId;
            const existingItem = prev[targetKey] || {};
            return {
                ...prev,
                [targetKey]: {
                    ...existingItem,
                    isError: true,
                    message: err?.message || 'Download failed',
                    details: err?.message || String(err),
                },
            };
        });
        sparkToast.error(err?.message || 'Download failed');
        console.error('Signed URL download error:', err);
    }
}

// Create Context
export const GlobalContext = createContext();

// Create the Provider Component
export const GlobalProvider = ({ children }) => {
    const [allUsecases, setAllUsecases] = useState([]);
    const [isFetchingUsecases, setIsFetchingUsecases] = useState(false);
    const [activityLog, setActivityLog] = useState([]);
    const [trainStatuses, setTrainStatus] = useReducer((state, trainStatus) => {
        // Reconciliation after a realtime gap (WS reconnect / tab wake): a
        // terminal status update missed while disconnected is never re-sent,
        // so a stale non-final entry would keep the training banner up
        // forever. Replay the server-confirmed terminal statuses over stale
        // entries. Never deletes entries and never applies non-final server
        // statuses, so optimistic "waiting" seeds (registerStartedTraining)
        // and rerun transitions (terminal -> waiting) are left intact.
        // WS events carry an `action` string field, never `type`.
        if (trainStatus?.type === 'syncFinalStatuses') {
            let next = null;
            for (const training of trainStatus.trainings) {
                const id = training.distillation_event_id;
                const current = id ? state[id] : null;
                if (
                    current &&
                    !FINAL_TRAINING_STATUSES.includes(current.status) &&
                    FINAL_TRAINING_STATUSES.includes(training.status)
                ) {
                    next = next ?? { ...state };
                    next[id] = {
                        ...current,
                        status: training.status,
                        progress: training.progress,
                        error_details: training.error_details ?? null,
                    };
                }
            }
            return next ?? state;
        }
        return { ...state, [trainStatus.distillation_event_id]: trainStatus };
    }, {});
    const [generationProgress, setGenerationProgress] = useState({});
    const [lastMessages, setLastMessages] = useState(null);
    const [progressStatus, setProgressStatus] = useState({});
    const [evaluationProgress, setEvaluationProgress] = useState({});
    const [modelPerfRunStatus, setModelPerfRunStatus] = useState({});
    // Signal (by usecase + major version) that evaluation results are ready.
    // Used to trigger a single fetch when the backend has finished writing all outputs.
    const [evaluationResultsReady, setEvaluationResultsReady] = useState({});
    const [cleanupCompleteSignal, setCleanupCompleteSignal] = useState(null);
    /** Fired as soon as the user cancels an in-flight upload (before WS cleanup completes). */
    const [uploadCancelInitiatedSignal, setUploadCancelInitiatedSignal] = useState(null);

    const [currentUsecaseId, setCurrentUsecaseId] = useState(null);
    const [currentUsecase, setCurrentUsecase] = useState(null);
    const [viewType, setViewTypeState] = useState('cards');

    // Ref to track last progress updates to prevent excessive re-renders
    const lastProgressUpdates = useRef(new Map());

    // Registry of abort functions for in-flight uploads, keyed by progress item key.
    const uploadAbortHandlers = useRef(new Map());
    /** Train/benchmark names whose cancel is "append to existing" — preserve Data Analysis until refetch. */
    const appendCancelPreserveAnalysisRef = useRef(new Set());

    // Keys of recently cancelled uploads, mapped to the cancel timestamp.
    // Used to suppress late-arriving WebSocket progress updates.
    const cancelledUploadKeys = useRef(new Map());
    const cancelledGenerationKeys = useRef(new Map());

    // Multi-objective Composed-Task uploads fan out into N atomic backend
    // events, each emitting its own per-clone progress over WebSocket. The
    // FloatingProgressBar must still render a single card per user-visible
    // upload, so we maintain two parallel maps:
    //   `uploadGroupChildToParent`: child clone name -> parent group key
    //   `uploadGroupChildren`:      parent group key -> Set<child clone name>
    // Both are populated by `registerUploadGroup` (called by UploadDataTab
    // before firing the upload) and torn down by `unregisterUploadGroup` /
    // `removeProgressItem`. Single-objective uploads never touch them.
    const uploadGroupChildToParent = useRef(new Map());
    const uploadGroupChildren = useRef(new Map());
    /** Restored from session + set on register; merged into coalesced parent rows after reload. */
    const uploadGroupMetadataByParent = useRef(new Map());

    /** Call when starting a new upload for this dataset/benchmark so progress is not suppressed (e.g. re-upload after cancel). */
    const clearCancelledUploadSuppression = useCallback((datasetName) => {
        if (datasetName != null && datasetName !== '') {
            cancelledUploadKeys.current.delete(datasetName);
        }
    }, []);

    /**
     * Register the child clone-dataset names that a multi-objective upload
     * will produce, so per-child WebSocket progress events route into the
     * single parent group entry the user sees in `FloatingProgressBar`.
     *
     * @param {string} groupKey
     * @param {string[]} childDatasetNames
     * @param {Object} [metadata] - persisted for post-refresh WS coalescing + task-step hints
     */
    const registerUploadGroup = useCallback((groupKey, childDatasetNames, metadata) => {
        if (!groupKey || !Array.isArray(childDatasetNames) || childDatasetNames.length === 0) {
            return;
        }
        const childSet = new Set();
        for (const child of childDatasetNames) {
            if (child) {
                uploadGroupChildToParent.current.set(child, groupKey);
                childSet.add(child);
            }
        }
        if (childSet.size > 0) {
            uploadGroupChildren.current.set(groupKey, childSet);
        }
        if (metadata && typeof metadata === 'object') {
            uploadGroupMetadataByParent.current.set(groupKey, { ...metadata });
        }
        upsertComposeUploadGroup(groupKey, [...childDatasetNames], metadata || {});
    }, []);

    /** Tear down a previously registered upload group. */
    const unregisterUploadGroup = useCallback((groupKey) => {
        if (!groupKey) return;
        removeComposeUploadGroup(groupKey);
        uploadGroupMetadataByParent.current.delete(groupKey);
        const children = uploadGroupChildren.current.get(groupKey);
        if (children) {
            for (const child of children) {
                if (uploadGroupChildToParent.current.get(child) === groupKey) {
                    uploadGroupChildToParent.current.delete(child);
                }
            }
        }
        uploadGroupChildren.current.delete(groupKey);
    }, []);
    /** Assigned after fetchUsecases is defined; used by cancelUpload without hook order issues. */
    const fetchUsecasesRef = useRef(null);

    const registerUploadAbort = useCallback((key, abortFn) => {
        uploadAbortHandlers.current.set(key, abortFn);
        cancelledUploadKeys.current.delete(key);
    }, []);

    const unregisterUploadAbort = useCallback((key) => {
        uploadAbortHandlers.current.delete(key);
    }, []);

    // Function to remove individual progress item
    const removeProgressItem = useCallback((datasetName) => {
        setProgressStatus(prev => {
            const newStatus = { ...prev };
            const removedItem = newStatus[datasetName];
            delete newStatus[datasetName];

            // Clean up progress tracking ref if this was a download item
            if (removedItem?.isDownload && removedItem?.usecaseId && removedItem?.version) {
                const progressKey = `${removedItem.usecaseId}_${removedItem.version}`;
                lastProgressUpdates.current.delete(progressKey);
            }

            return newStatus;
        });
        removeComposeUploadGroup(datasetName);
        // If this was a multi-objective upload group, drop the child mapping
        // too so a future re-upload with the same name routes correctly.
        if (uploadGroupChildren.current.has(datasetName)) {
            const children = uploadGroupChildren.current.get(datasetName);
            if (children) {
                for (const child of children) {
                    if (uploadGroupChildToParent.current.get(child) === datasetName) {
                        uploadGroupChildToParent.current.delete(child);
                    }
                }
            }
            uploadGroupChildren.current.delete(datasetName);
            uploadGroupMetadataByParent.current.delete(datasetName);
        }
    }, []);

    /** Restore composed-task upload group routing after full page reload (sessionStorage). */
    useEffect(() => {
        const groups = loadComposeUploadGroups();
        for (const [parentKey, entry] of Object.entries(groups)) {
            if (!entry?.children?.length) continue;
            const childSet = new Set();
            for (const child of entry.children) {
                if (child) {
                    uploadGroupChildToParent.current.set(child, parentKey);
                    childSet.add(child);
                }
            }
            if (childSet.size > 0) {
                uploadGroupChildren.current.set(parentKey, childSet);
            }
            if (entry.metadata && typeof entry.metadata === 'object') {
                uploadGroupMetadataByParent.current.set(parentKey, entry.metadata);
            }
        }
    }, []);

    const cancelUpload = useCallback((key) => {
        const abortFn = uploadAbortHandlers.current.get(key);
        if (abortFn) {
            abortFn();
            uploadAbortHandlers.current.delete(key);
        }

        // Notify the backend to mark the upload as cancelled and clean up.
        // This covers both file uploads in the processing phase and
        // HuggingFace uploads where server-side work is already in progress.
        const item = progressStatus[key];
        if (item && !item.isDownload && !item.isCompleted && !item.isError) {
            const benchmarkLikeKey =
                typeof key === 'string' &&
                /_test(?:__t_[A-Za-z0-9]+)?$/.test(key);
            const isBenchmark = !!(item.isBenchmark || benchmarkLikeKey);
            let clientId = item.clientId || item.client_id;
            if (!clientId && item.usecaseId && allUsecases.length) {
                const uc = allUsecases.find((u) => u._id === item.usecaseId);
                clientId = uc?.client_id;
            }
            clientId = clientId || '40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75';

            // Multi-objective uploads: cancel each child clone individually
            // so each per-subtask collection cleans itself up. Single uploads
            // fall through to the legacy single-cancel call below.
            const groupChildren = uploadGroupChildren.current.get(key);
            const cancelUsecaseId = item.usecaseId || currentUsecaseId;
            if (groupChildren && groupChildren.size > 0) {
                for (const childDatasetName of groupChildren) {
                    if (cancelUsecaseId && !item.existedOnUsecaseBeforeUpload) {
                        appendPendingUploadCleanup({
                            usecaseId: cancelUsecaseId,
                            datasetName: childDatasetName,
                            isBenchmark,
                        });
                    }
                    cancelledUploadKeys.current.set(childDatasetName, Date.now());
                    data_from_server_post(
                        'cancel-upload',
                        {
                            client_id: clientId,
                            dataset_name: childDatasetName,
                            usecase_id: cancelUsecaseId || null,
                            is_benchmark: isBenchmark,
                            remove_from_usecase: item.existedOnUsecaseBeforeUpload !== true,
                        },
                        (resp) => {
                            if (resp?.success) {
                                fetchUsecasesRef.current?.();
                                setTimeout(() => fetchUsecasesRef.current?.(), 500);
                            }
                        },
                        () => { /* best-effort */ }
                    );
                }
                setUploadCancelInitiatedSignal({
                    datasetName: key,
                    isBenchmark,
                    ts: Date.now(),
                    existedOnUsecaseBeforeUpload: !!item.existedOnUsecaseBeforeUpload,
                });
            } else {
                const datasetName = item.datasetName || key;
                if (cancelUsecaseId && !item.existedOnUsecaseBeforeUpload) {
                    appendPendingUploadCleanup({
                        usecaseId: cancelUsecaseId,
                        datasetName,
                        isBenchmark,
                    });
                }
                if (item.existedOnUsecaseBeforeUpload) {
                    appendCancelPreserveAnalysisRef.current.add(datasetName);
                }
                setUploadCancelInitiatedSignal({
                    datasetName,
                    isBenchmark,
                    ts: Date.now(),
                    existedOnUsecaseBeforeUpload: !!item.existedOnUsecaseBeforeUpload,
                });

                data_from_server_post(
                    'cancel-upload',
                    {
                        client_id: clientId,
                        dataset_name: datasetName,
                        // Must match cancelUsecaseId (fallback to current task) so backend cleanup
                        // receives usecase_id and can resync llm_usecase_info.label_names_count.
                        usecase_id: cancelUsecaseId || null,
                        is_benchmark: isBenchmark,
                        // Append-to-existing upload: keep the name on the usecase; net-new upload: remove from usecase.
                        remove_from_usecase: item.existedOnUsecaseBeforeUpload !== true,
                    },
                    (resp) => {
                        // Sync Mongo (e.g. $pull after cancel) into React; avoids ghost datasets / refetch loops.
                        if (resp?.success) {
                            fetchUsecasesRef.current?.();
                            setTimeout(() => fetchUsecasesRef.current?.(), 500);
                        }
                    },
                    () => { /* best-effort */ }
                );
            }
        }

        // Suppress late-arriving WebSocket updates for this key (expires after 60 s).
        cancelledUploadKeys.current.set(key, Date.now());

        removeProgressItem(key);
    }, [removeProgressItem, progressStatus, allUsecases, currentUsecaseId]);

    const cancelGeneration = useCallback((key, { keepGeneratedRecords = true } = {}) => {
        const item = progressStatus[key];
        const usecaseId = item?.usecaseId;
        cancelledGenerationKeys.current.set(key, Date.now());
        if (usecaseId) {
            cancelledGenerationKeys.current.set(
                aiGenerationProgressKey(usecaseId, item?.datasetName),
                Date.now()
            );
        }
        const otherActiveGeneration = Object.entries(progressStatus).some(
            ([progressKey, progressItem]) =>
                progressKey !== key
                && progressItem?.isGeneration
                && progressItem.usecaseId === usecaseId
                && !progressItem.isCompleted
                && !progressItem.isError
        );
        removeProgressItem(key);
        if (usecaseId && item?.datasetName) {
            const datasetKey = aiGenerationProgressKey(usecaseId, item.datasetName);
            if (datasetKey !== key) removeProgressItem(datasetKey);
        }
        if (usecaseId && !otherActiveGeneration) {
            setGenerationProgress((prev) => {
                const next = { ...prev };
                delete next[usecaseId];
                return next;
            });
        }
        if (!item?.isGeneration || !usecaseId) {
            return;
        }
        let clientId = item.clientId || item.client_id;
        if (!clientId && allUsecases.length) {
            const uc = allUsecases.find((u) => u._id === usecaseId);
            clientId = uc?.client_id;
        }
        data_from_server_post(
            'ai-generation/cancel',
            buildCancelGenerationRequest({
                usecaseId,
                datasetName: item.datasetName,
                clientId,
                keepGeneratedRecords,
            }),
            (resp) => {
                if (resp?.success) {
                    fetchUsecasesRef.current?.();
                }
            },
            () => { /* best-effort; the job flag still stops the handler */ }
        );
    }, [progressStatus, allUsecases, removeProgressItem]);

    /**
     * Begin the actual save-to-disk for a previously prepared model artifact.
     *
     * Must be called from a click handler — this is the user-gesture window
     * during which ``showSaveFilePicker`` is allowed to open. Acquiring the
     * picker handle BEFORE the network request also means we never accumulate
     * any bytes in memory: chunks stream straight from the response body to the
     * picked file.
     *
     * Picker-unsupported browsers (Firefox, Safari, older Chromium) hit the
     * ``PickerUnavailableError`` branch and fall back to a native browser
     * download via the GET ``/download-model`` route, which is the only path
     * that's safe without File System Access — the browser's own download
     * manager handles bytes-to-disk and never touches page memory.
     */
    const triggerModelDownloadSave = useCallback(async (progressKey) => {
        const item = progressStatus[progressKey];
        if (!item || !item.isDownload) {
            return;
        }
        if (!item.downloadPath || !item.filename) {
            return;
        }
        const artifactType = item.artifactType || 'zip';
        const fileExtension = artifactType === 'docker' ? '.tar' : '.zip';
        const filename = item.filename.endsWith(fileExtension)
            ? item.filename
            : `${item.filename}${fileExtension}`;

        // Docker tars are multi-GB. Chrome's File System Access + fetch stream
        // dies with "network error" around 4GB / 10 min. Use the native
        // download manager (GET /download-model → signed object-storage URL).
        const useNativeBrowserDownload = artifactType === 'docker';
        let savedHandle;
        if (useNativeBrowserDownload) {
            savedHandle = undefined;
        } else {
            try {
                savedHandle = await requestSaveTarget(filename);
            } catch (err) {
                if (err instanceof PickerUnavailableError) {
                    savedHandle = undefined;
                } else {
                    throw err;
                }
            }
        }

        if (savedHandle === null) {
            // User cancelled the save dialog — leave the row as ``ready_to_save``
            // so they can click again.
            setProgressStatus(prev => {
                const existing = prev[progressKey];
                if (!existing) return prev;
                return {
                    ...prev,
                    [progressKey]: {
                        ...existing,
                        message: 'Save cancelled — click "Save to disk" to retry',
                    },
                };
            });
            return;
        }

        // Switch the progress entry into the ``downloading`` phase before the
        // network request so the UI shows the correct step + 0% immediately.
        setProgressStatus(prev => {
            const existing = prev[progressKey];
            if (!existing) return prev;
            return {
                ...prev,
                [progressKey]: {
                    ...existing,
                    phase: 'downloading',
                    buildProgress: 100,
                    downloadProgress: 0,
                    downloadIndeterminate: !savedHandle,
                    isCompleted: false,
                    isError: false,
                    progress: 100,
                    message: savedHandle
                        ? 'Downloading to your computer…'
                        : 'Downloading via browser – check the toolbar for progress',
                },
            };
        });

        const eventLike = {
            usecaseId: item.usecaseId,
            version: item.version,
            client_id: item.clientId,
            downloadPath: item.downloadPath,
            filename: item.filename,
            artifactType,
            sizeBytes: item.sizeBytes,
        };

        await startModelArtifactDownload({
            event: eventLike,
            fileExtension,
            downloadId: progressKey,
            setProgressStatus,
            savedHandle,
        });
    }, [progressStatus]);

    // Group flat usecases into the { group_name, sub_groups: [{ sub_group_name, usecases }] }
    // shape. Multiple usecases sharing the same `sub_group_name` are one Task
    // with several objectives. Single-objective rows that pre-date the
    // `sub_group_name` backfill (or were created before the field existed)
    // fall back to `usecase_name` so each one still maps to its own task
    // bucket — never an unaddressable shared "" bucket.
    const allApps = useMemo(() => {
        const appGroups = _.groupBy(allUsecases, 'group_name');
        return Object.entries(appGroups).map(([groupName, usecases]) => {
            const subGroupBuckets = _.groupBy(
                usecases,
                (u) => (u.sub_group_name && u.sub_group_name.trim()) || u.usecase_name
            );
            const sub_groups = Object.entries(subGroupBuckets).map(([subGroupName, subUsecases]) => ({
                sub_group_name: subGroupName,
                usecases: subUsecases,
            }));
            return {
                group_name: groupName,
                project_goal: usecases[0]?.project_goal || '',
                sub_groups,
            };
        });
    }, [allUsecases]);

    // Sub-group bucket that owns `currentUsecase`, or null. Used for the task
    // landing page (`/task/...`) and per-objective switching in the header.
    // Single-objective tasks resolve here too (their bucket key falls back to
    // `usecase_name` in the bucketing above).
    const currentSubGroup = useMemo(() => {
        if (!currentUsecase) return null;
        const app = allApps.find((a) => a.group_name === currentUsecase.group_name);
        if (!app) return null;
        const usecaseKey = (currentUsecase.sub_group_name && currentUsecase.sub_group_name.trim())
            || currentUsecase.usecase_name;
        return (app.sub_groups || []).find((sg) => sg.sub_group_name === usecaseKey) || null;
    }, [allApps, currentUsecase]);

    // `currentSubGroupKey` is a lightweight identity for the active task
    // sub-group — handy as a dependency for memos / effects without including
    // the whole sub-group object.
    const currentSubGroupKey = useMemo(() => {
        if (!currentSubGroup || !currentUsecase) return null;
        return {
            group_name: currentUsecase.group_name,
            sub_group_name: currentSubGroup.sub_group_name,
        };
    }, [currentSubGroup, currentUsecase]);

    const updateUsecase = useCallback((usecaseId, update) => {
        setAllUsecases((allUsecases) => {
            return allUsecases.map(usecase => {
                if (usecase._id === usecaseId) {
                    return { ...usecase, ...update };
                }
                return usecase;
            });
        });
    }, []);

    const updateStatus = useCallback((usecaseId, status, deployedVersion = null, isDeployed = null) => {
        setAllUsecases(prevUsecases =>
            prevUsecases.map(usecase => {
                if (usecase._id === usecaseId) {
                    let extra_data = {}
                    if (status === deployed) {
                        extra_data = {
                            deployed_version: deployedVersion !== null ? deployedVersion : usecase.version,
                            is_deployed: isDeployed !== null ? isDeployed : true,
                        }
                    } else if (status === ready_to_deploy) {
                        // When status is ready_to_deploy, use explicit values from WebSocket if provided
                        // Otherwise, if coming from undeploy_in_progress, clear deployment info
                        if (isDeployed !== null) {
                            // Use explicit is_deployed value from WebSocket
                            extra_data = {
                                deployed_version: deployedVersion !== null ? deployedVersion : (isDeployed ? usecase.deployed_version : null),
                                is_deployed: isDeployed,
                            }
                        } else if (usecase.status === undeploy_in_progress) {
                            // Coming from undeploy, clear deployment info
                            extra_data = {
                                deployed_version: null,
                                is_deployed: false,
                            }
                        }
                    } else if (isDeployed !== null || deployedVersion !== null) {
                        // For any other status, if isDeployed or deployedVersion is explicitly provided, use them
                        extra_data = {
                            deployed_version: deployedVersion !== null ? deployedVersion : usecase.deployed_version,
                            is_deployed: isDeployed !== null ? isDeployed : usecase.is_deployed,
                        }
                    }
                    return { ...usecase, status, ...extra_data };
                }
                return usecase;
            })
        );
    }, [deployed, ready_to_deploy, undeploy_in_progress]);

    const markTrainingStatusCanceled = useCallback((distillationEventId, usecaseId) => {
        if (!distillationEventId) return;
        setTrainStatus({
            distillation_event_id: distillationEventId,
            usecase_id: usecaseId,
            llm_usecase_id: usecaseId,
            status: TRAINING_STATUSES.CANCELED,
            progress: 0,
            error_details: null,
        });
    }, []);

    // Seed the global trainStatuses map the moment a training is created, so
    // every useTrainingProgress instance sees it immediately — the task page
    // header banner often lives in a different component tree than the train
    // screen that started the training (and may only mount after navigating
    // there), while the first WebSocket status update can lag minutes behind
    // until the training job actually spins up. Later WS updates overwrite
    // this entry by distillation_event_id.
    const registerStartedTraining = useCallback((distillationEventId, usecaseId) => {
        if (!distillationEventId) return;
        setTrainStatus({
            distillation_event_id: distillationEventId,
            usecase_id: usecaseId,
            llm_usecase_id: usecaseId,
            status: TRAINING_STATUSES.WAITING,
            progress: 0,
            error_details: null,
        });
    }, []);

    // Overwrite stale non-final trainStatuses entries with server-confirmed
    // terminal statuses (see the syncFinalStatuses reducer branch above).
    // Called with the /trainings fetch result after a realtime gap.
    const syncFinalTrainStatuses = useCallback((trainings) => {
        if (!Array.isArray(trainings) || trainings.length === 0) return;
        setTrainStatus({ type: 'syncFinalStatuses', trainings });
    }, []);

    // WebSocket delivery is at-most-once: updates published while a socket is
    // down (or half-open in a sleeping tab) are never re-sent. Bumping this
    // tick tells data hooks to refetch realtime-fed state from the server.
    // Debounced because visibilitychange fires on every tab switch.
    const [realtimeResyncTick, setRealtimeResyncTick] = useState(0);
    const lastRealtimeResyncAt = useRef(0);
    const requestRealtimeResync = useCallback(() => {
        const now = Date.now();
        if (now - lastRealtimeResyncAt.current < 5000) return;
        lastRealtimeResyncAt.current = now;
        setRealtimeResyncTick(now);
    }, []);

    // Auto-train after data flows: the terminal-event evaluation is defined
    // after progressStatusRef (below) and reached through this ref so
    // handleWsMessage can call it without a use-before-define.
    const evaluateAutoTrainTerminalRef = useRef(null);
    const allUsecasesRef = useRef([]);
    useEffect(() => {
        allUsecasesRef.current = allUsecases;
    }, [allUsecases]);

    const handleWsMessage = useCallback((event) => {
        switch (event.action) {
            case "updateStatus":
                if (event.message) {
                    setLastMessages(event);
                }
                if (event.usecaseId) {
                    updateStatus(
                        event.usecaseId,
                        event.status,
                        event.deployed_version,
                        event.is_deployed
                    )
                }
                break;
            case "updateProgress": {
                // Multi-objective uploads emit per-child WS events; route them
                // into the parent group's progress entry so the user sees a
                // single FloatingProgressBar card. Single-objective uploads
                // hit `parentKey === event.datasetName` and behave as before.
                const parentKey = uploadGroupChildToParent.current.get(event.datasetName)
                    || event.datasetName;
                const isCoalesced = parentKey !== event.datasetName;

                const cancelledAt = cancelledUploadKeys.current.get(parentKey)
                    || cancelledUploadKeys.current.get(event.datasetName);
                if (cancelledAt && (Date.now() - cancelledAt < 60_000)) {
                    break;
                }
                if (cancelledAt) {
                    cancelledUploadKeys.current.delete(parentKey);
                    cancelledUploadKeys.current.delete(event.datasetName);
                }
                setProgressStatus(prev => {
                    const existing = prev[parentKey] || {};
                    if (existing?.isError && event.progress > 0) {
                        return prev;
                    }

                    // If the UI is tracking client-side upload progress, map processing progress (0..100)
                    // into the second half of the overall bar (50..100).
                    const hasClientUploadPhase =
                        existing?.phase === 'uploading' ||
                        existing?.phase === 'processing' ||
                        typeof existing?.uploadProgress === 'number';

                    const eventProgress = typeof event.progress === 'number' ? event.progress : 0;

                    // For coalesced (multi-objective) groups, track per-child
                    // progress on the parent entry and report the slowest
                    // sibling. Bar advances only when ALL children advance,
                    // and the group completes only when every expected child
                    // either finishes or errors.
                    let processingProgress = eventProgress;
                    let aggregatedIsCompleted = !!event.isCompleted;
                    let aggregatedIsError = !!event.isError;
                    let nextChildProgresses = existing?.childProgresses;
                    let nextChildErrors = existing?.childErrors;
                    if (isCoalesced) {
                        const childCount = uploadGroupChildren.current.get(parentKey)?.size
                            || Object.keys({
                                ...(existing?.childProgresses || {}),
                                [event.datasetName]: eventProgress,
                            }).length;
                        const merged = mergeCoalescedGroupChildIntoParent(
                            existing,
                            {
                                childCloneName: event.datasetName,
                                childProgress: eventProgress,
                                childIsError: !!event.isError,
                            },
                            childCount
                        );
                        nextChildProgresses = merged.nextChildProgresses;
                        nextChildErrors = merged.nextChildErrors;
                        processingProgress = merged.processingProgress;
                        aggregatedIsCompleted = merged.aggregatedIsCompleted;
                        aggregatedIsError = merged.aggregatedIsError;
                    }

                    const processingDone = processingProgress >= 100 || aggregatedIsCompleted;

                    // For AI-labeled single-objective uploads, transition to label_generation phase
                    // instead of completing once raw processing reaches 100%. Multi-objective
                    // uploads do not use the label-generation flow.
                    const isLabelGen =
                        !isCoalesced && existing?.isLabelGeneration && !aggregatedIsError;
                    // Deferred wizard AI labeling waits for Model Config (and the
                    // generation WS fold-in) — do not optimistic-transition yet.
                    const transitionToLabelGen =
                        isLabelGen
                        && processingDone
                        && existing?.phase !== 'label_generation'
                        && (!existing?.deferLabelGeneration || existing?.labelGenerationArmed);
                    const holdForDeferredGen =
                        isLabelGen
                        && existing?.deferLabelGeneration
                        && !existing?.labelGenerationArmed
                        && processingDone
                        && existing?.phase !== 'label_generation';

                    const overallProgress = hasClientUploadPhase
                        ? Math.max(50, Math.min(100, 50 + (processingProgress / 100) * 50))
                        : processingProgress;

                    const keepLabelGenOpen =
                        existing?.phase === 'label_generation' && !aggregatedIsError && !existing?.isCompleted;

                    const nextMessage = (() => {
                        if (keepLabelGenOpen) return existing?.message || 'Generating labels';
                        if (transitionToLabelGen) return 'Generating labels';
                        if (holdForDeferredGen) return 'Processing complete';
                        if (isCoalesced) {
                            if (aggregatedIsError) return existing?.message || 'Processing failed';
                            if (aggregatedIsCompleted) return 'Processing complete';
                            if (hasClientUploadPhase) return 'Processing dataset';
                            return existing?.message || event.message || 'Processing dataset';
                        }
                        if (event.message) return event.message;
                        if (!hasClientUploadPhase) return existing?.message || '';
                        if (event.isError) return existing?.message || 'Processing failed';
                        if (processingDone) return 'Processing complete';
                        return 'Processing file';
                    })();

                    const nextPhase = (() => {
                        if (!hasClientUploadPhase) return existing?.phase;
                        if (transitionToLabelGen) return 'label_generation';
                        // Keep the card open (and label-gen step pending) until
                        // Model Config arms generation / WS fold-in starts it.
                        if (holdForDeferredGen) return 'processing';
                        if (existing?.phase === 'label_generation' && !aggregatedIsError && !existing?.isCompleted) {
                            return 'label_generation';
                        }
                        if (processingDone) return 'completed';
                        return 'processing';
                    })();

                    const groupMeta = isCoalesced ? uploadGroupMetadataByParent.current.get(parentKey) : null;
                    const coalescedMetaPatch = {};
                    if (isCoalesced && groupMeta && typeof groupMeta === 'object') {
                        if (
                            Array.isArray(groupMeta.affectedUsecaseIds) &&
                            groupMeta.affectedUsecaseIds.length > 0 &&
                            !(Array.isArray(existing?.affectedUsecaseIds) && existing.affectedUsecaseIds.length > 0)
                        ) {
                            coalescedMetaPatch.affectedUsecaseIds = [...groupMeta.affectedUsecaseIds];
                        }
                        if (groupMeta.clientId != null && existing?.clientId == null) {
                            coalescedMetaPatch.clientId = groupMeta.clientId;
                        }
                        if (groupMeta.isBenchmark != null && existing?.isBenchmark == null) {
                            coalescedMetaPatch.isBenchmark = groupMeta.isBenchmark;
                        }
                    }

                    // Rows skipped during server-side parsing (e.g. prompt-template
                    // extraction failures). Reported once by the metadata pass;
                    // persist across later progress events so the completed card
                    // can show a partial-success warning with the exact count.
                    const skippedRecords =
                        typeof event.skippedRecords === 'number' && event.skippedRecords > 0
                            ? event.skippedRecords
                            : existing?.skippedRecords;
                    const totalFileRecords =
                        typeof event.totalFileRecords === 'number' && event.totalFileRecords > 0
                            ? event.totalFileRecords
                            : existing?.totalFileRecords;
                    const skipWarningFields =
                        skippedRecords > 0 && aggregatedIsCompleted && !aggregatedIsError
                            ? {
                                isWarning: true,
                                warningTitle: "Some examples couldn't be extracted",
                                warningMessage: totalFileRecords > 0
                                    ? `${skippedRecords} of ${totalFileRecords} records could not be extracted using the prompt template and were skipped.`
                                    : `${skippedRecords} records could not be extracted using the prompt template and were skipped.`,
                            }
                            : {};

                    const wasCoalescedTerminal = Boolean(
                        isCoalesced && (existing.isCompleted || existing.isError)
                    );
                    const nowCoalescedTerminal = Boolean(
                        isCoalesced && (aggregatedIsCompleted || aggregatedIsError) && !transitionToLabelGen
                    );
                    if (nowCoalescedTerminal && !wasCoalescedTerminal) {
                        queueMicrotask(() => {
                            unregisterUploadGroup(parentKey);
                        });
                    }

                    const next = {
                        ...prev,
                        [parentKey]: {
                            ...existing,
                            ...coalescedMetaPatch,
                            progress: transitionToLabelGen || holdForDeferredGen || keepLabelGenOpen ? 99 : overallProgress,
                            isCompleted: (transitionToLabelGen || holdForDeferredGen || keepLabelGenOpen) ? false : aggregatedIsCompleted,
                            isError: aggregatedIsError,
                            datasetName: isCoalesced
                                ? (existing?.datasetName || groupMeta?.datasetDisplayName || parentKey)
                                : parentKey,
                            usecaseId: existing?.usecaseId ?? groupMeta?.usecaseIdPrimary ?? event.usecaseId,
                            // Preserve client-side fields (uploadProgress/phase) and fill in processing progress.
                            uploadProgress: hasClientUploadPhase ? (existing?.uploadProgress ?? 100) : existing?.uploadProgress,
                            processingProgress: hasClientUploadPhase ? processingProgress : existing?.processingProgress,
                            phase: nextPhase,
                            message: nextMessage,
                            details: event.details ?? existing?.details,
                            ...(skippedRecords > 0 ? { skippedRecords, totalFileRecords } : {}),
                            ...skipWarningFields,
                            ...(isCoalesced
                                ? { childProgresses: nextChildProgresses, childErrors: nextChildErrors }
                                : {}),
                        }
                    };
                    return foldDatasetProgressIntoRetrievalAgent(next, {
                        datasetName: event.datasetName,
                        progressKey: parentKey,
                        isCompleted: Boolean(next[parentKey]?.isCompleted),
                        isError: Boolean(next[parentKey]?.isError),
                    });
                });
                // Auto-train after data flows: mirror a terminal upload locally
                // (backend_consumer makes the authoritative decision on the
                // same DatasetProgressUpdate event; a delayed refetch inside
                // evaluateAutoTrainAfterTerminal reconciles the doc).
                if ((event.isCompleted || event.isError) && event.usecaseId) {
                    const uploadFlow = {
                        flowType: 'upload',
                        datasetName: event.datasetName,
                        status: event.isError ? 'failed' : 'completed',
                        error: event.details || event.message,
                    };
                    setAllUsecases((prev) => prev.map((usecase) => {
                        if (usecase._id !== event.usecaseId) return usecase;
                        const nextAutoTrain = applyAutoTrainFlowTerminal(
                            usecase.auto_train_pending,
                            uploadFlow,
                        );
                        if (nextAutoTrain === usecase.auto_train_pending) return usecase;
                        return { ...usecase, auto_train_pending: nextAutoTrain };
                    }));
                    evaluateAutoTrainTerminalRef.current?.(event.usecaseId, uploadFlow);
                }
                break;
            }
            case "updateDownloadProgress": {
                const artifactType = event.artifactType || 'zip';
                const ecrPhased =
                    (artifactType === 'ecr' ||
                        artifactType === 'gcr' ||
                        artifactType === 'dockerhub') &&
                    typeof event.progress === 'number'
                        ? mapEcrOverallProgress(event.progress)
                        : null;
                setProgressStatus(prev => ({
                    ...prev,
                    [event.downloadId]: {
                        progress: event.progress,
                        ...(ecrPhased || {}),
                        isCompleted: !!event.isCompleted,
                        isError: !!event.isError,
                        datasetName: event.modelName || `Model v${event.version}`,
                        usecaseId: event.usecaseId,
                        message: event.message,
                        details: event.details,
                        isDownload: true,
                        downloadId: event.downloadId,
                        version: event.version,
                        clientId: event.clientId,
                        downloadPath: event.downloadPath,
                        filename: event.filename,
                        artifactType,
                    }
                }));
                break;
            }
            case "uploadCleanupComplete": {
                if (
                    event.usecaseId &&
                    currentUsecaseId &&
                    event.usecaseId !== currentUsecaseId
                ) {
                    break;
                }
                const cleanupUsecaseId = event.usecaseId || currentUsecaseId;
                if (cleanupUsecaseId && event.datasetName != null) {
                    removePendingUploadCleanup({
                        usecaseId: cleanupUsecaseId,
                        datasetName: event.datasetName,
                        isBenchmark: !!event.isBenchmark,
                    });
                }
                setCleanupCompleteSignal({
                    datasetName: event.datasetName,
                    isBenchmark: !!event.isBenchmark,
                    usecaseId: event.usecaseId || null,
                    ts: Date.now(),
                });
                // Auto-train after data flows: a cancelled upload is a failed
                // flow — mirror it so the docked card / toasts react without
                // waiting for the refetch (the /cancel-upload route made the
                // authoritative call already).
                if (cleanupUsecaseId && event.datasetName != null) {
                    const cancelledUploadFlow = {
                        flowType: 'upload',
                        datasetName: event.datasetName,
                        status: 'cancelled',
                        error: 'Upload cancelled',
                    };
                    setAllUsecases((prev) => prev.map((usecase) => {
                        if (usecase._id !== cleanupUsecaseId) return usecase;
                        const nextAutoTrain = applyAutoTrainFlowTerminal(
                            usecase.auto_train_pending,
                            cancelledUploadFlow,
                        );
                        if (nextAutoTrain === usecase.auto_train_pending) return usecase;
                        return { ...usecase, auto_train_pending: nextAutoTrain };
                    }));
                    evaluateAutoTrainTerminalRef.current?.(cleanupUsecaseId, cancelledUploadFlow);
                }
                break;
            }
            case "showMessage":
                setLastMessages(event);
                break;
            case "updateTrainingStatus":
                setTrainStatus(event);
                break;
            case "updateModelPerfRunStatus": {
                const perfKey = `${event.usecase_id}_${event.model_version}`;
                setModelPerfRunStatus((prev) => ({
                    ...prev,
                    [perfKey]: {
                        run_id: event.run_id,
                        status: event.status,
                        usecase_id: event.usecase_id,
                        model_version: event.model_version,
                        estimated_cost_usd: event.estimated_cost_usd,
                        estimated_duration_minutes: event.estimated_duration_minutes,
                        error: event.error,
                    },
                }));
                break;
            }
            case "updateEvaluationStatus":
                // Handle dedicated evaluation progress updates.
                // When sub_task_type is set (e.g. internal / benchmark), key by it so we show both separately.
                const progressKey = event.sub_task_type
                    ? `${event.usecase_id}_${event.version}_${event.sub_task_type}`
                    : `${event.usecase_id}_${event.version}`;
                setEvaluationProgress(prev => ({
                    ...prev,
                    [progressKey]: {
                        progress: event.progress,
                        status: event.status,
                        usecase_id: event.usecase_id,
                        distillation_event_id: event.distillation_event_id,
                        error_details: event.error_details,
                        step_name: event.step_name,
                        version: event.version,
                        origin_version: event.origin_version,
                        current_step: event.current_step,
                        total_steps: event.total_steps,
                        sub_task_type: event.sub_task_type,
                    }
                }));
                if (event.status === "completed" || event.status === "failed") {
                    const major = Math.floor(Number(event.version));
                    if (Number.isFinite(major)) {
                        setEvaluationResultsReady(prev => ({
                            ...prev,
                            [`${event.usecase_id}_${major}`]: {
                                ts: Date.now(),
                                version: event.version,
                                distillation_event_id: event.distillation_event_id,
                                failed: event.status === "failed",
                            }
                        }));
                    }
                }
                // Failed evaluation errors are shown inline in EvaluationProcessingState
                // (via the "details" link) rather than as a toast notification.
                break;
            case "updateActivity":
                setActivityLog((activityLog) => [...activityLog, event])
                break;
            case "updateTeacherEvaluationStatus":
                const { usecase_id: teacherUsecaseId, action, ...incoming } = event;
                let teacherMajor = null;
                setAllUsecases((prev) =>
                    prev.map((usecase) => {
                        if (usecase._id !== teacherUsecaseId) return usecase;
                        teacherMajor = Math.floor(Number(usecase?.version));
                        const existing = usecase.last_teacher_evaluation_status || {};
                        return {
                            ...usecase,
                            last_teacher_evaluation_status: mergeTeacherEvaluationStatus(existing, incoming),
                        };
                    })
                );
                // Use the existing evaluation refresh mechanism (evaluationResultsReady)
                // to refetch evaluation when teacher metrics become available
                // (or the run reached a terminal failed/cancelled state).
                if (
                    incoming?.is_completed ||
                    incoming?.is_cancelled ||
                    (incoming?.error && !incoming?.is_running)
                ) {
                    if (Number.isFinite(teacherMajor)) {
                        setEvaluationResultsReady((prev) => ({
                            ...prev,
                            [`${teacherUsecaseId}_${teacherMajor}`]: {
                                ts: Date.now(),
                                distillation_event_id: incoming?.distillation_event_id,
                                teacher: true,
                            },
                        }));
                    }
                }
                if (incoming?.error && !incoming?.is_running) {
                    sparkToast.error("Teacher evaluation failed");
                } else if (incoming?.is_cancelled && !incoming?.is_running) {
                    sparkToast.info("Teacher evaluation cancelled");
                }
                break;
            case "downloadCompletionUpdate":
                if (event.client_uuid !== getOrCreateClientUUID()) {
                    break;
                }
                if (event.isCompleted && !event.isError) {
                    // Find the existing progress item by matching usecaseId and version
                    const downloadId = event.downloadId || `download_${event.usecaseId}_${event.version}`;

                    const findExistingKey = (prev) =>
                        resolveDownloadProgressKey(prev, event, downloadId);

                    // ECR / GCR / Docker Hub push has no file to save: the image now
                    // lives in the customer's registry. Mark the item complete
                    // directly (no download_path / "Save to disk" step).
                    if (
                        event.artifactType === 'ecr' ||
                        event.artifactType === 'gcr' ||
                        event.artifactType === 'dockerhub'
                    ) {
                        setProgressStatus(prev => {
                            const existingKey = findExistingKey(prev);
                            const targetKey = existingKey || downloadId;
                            const existingItem = existingKey ? prev[existingKey] : null;
                            const registryLabel =
                                event.artifactType === 'gcr'
                                    ? 'GCR'
                                    : event.artifactType === 'dockerhub'
                                      ? 'Docker Hub'
                                      : 'ECR';
                            return {
                                ...prev,
                                [targetKey]: {
                                    ...existingItem,
                                    phase: 'complete',
                                    buildProgress: 100,
                                    downloadProgress: 100,
                                    progress: 100,
                                    isCompleted: true,
                                    isError: false,
                                    message: event.message || `Image pushed to ${registryLabel}.`,
                                    isDownload: true,
                                    downloadId: targetKey,
                                    usecaseId: event.usecaseId,
                                    version: event.version,
                                    clientId: event.client_id,
                                    datasetName:
                                        existingItem?.datasetName || `Model v${event.version}`,
                                    artifactType: event.artifactType,
                                },
                            };
                        });
                        break;
                    }

                    setProgressStatus(prev => {
                        const existingKey = findExistingKey(prev);
                        const existingItem = existingKey ? prev[existingKey] : null;
                        const targetKey = existingKey || downloadId;

                        const backendProgress = typeof event.progress === 'number'
                            ? event.progress
                            : 100;
                        const previousBuild = typeof existingItem?.buildProgress === 'number'
                            ? existingItem.buildProgress
                            : typeof existingItem?.progress === 'number'
                                ? existingItem.progress
                                : 0;
                        const buildProgress = Math.max(backendProgress, previousBuild);

                        return {
                            ...prev,
                            [targetKey]: {
                                ...existingItem,
                                phase: 'building',
                                buildProgress,
                                downloadProgress: existingItem?.downloadProgress ?? 0,
                                progress: buildProgress,
                                isCompleted: false,
                                message: event.message || "Model prepared successfully. Starting download...",
                                downloadPath: event.downloadPath,
                                filename: event.filename,
                                isDownload: true,
                                downloadId: targetKey,
                                usecaseId: event.usecaseId,
                                version: event.version,
                                clientId: event.client_id,
                                datasetName: existingItem?.datasetName || `Model v${event.version}`,
                                artifactType: event.artifactType || existingItem?.artifactType || 'zip',
                                sizeBytes: event.sizeBytes ?? existingItem?.sizeBytes,
                                localFilePath: event.localFilePath ?? existingItem?.localFilePath,
                            }
                        };
                    });

                    // Short-circuit: if the kafka completion event already carries the
                    // absolute local file path (local storage), skip the POST to
                    // /download-model entirely. Calling it would let a stale backend
                    // stream the multi-GB tar back as a binary response and OOM the
                    // renderer when fetch tries to buffer it for `.json()`.
                    if (event.localFilePath) {
                        setProgressStatus(prev => {
                            const existingKey = findExistingKey(prev);
                            const targetKey = existingKey || downloadId;
                            return {
                                ...prev,
                                [targetKey]: {
                                    ...prev[targetKey],
                                    phase: 'complete',
                                    buildProgress: 100,
                                    downloadProgress: 100,
                                    progress: 100,
                                    isCompleted: true,
                                    message: "File saved on disk – click to copy path",
                                    localFilePath: event.localFilePath,
                                    sizeBytes: event.sizeBytes ?? prev[targetKey]?.sizeBytes,
                                }
                            };
                        });
                        break;
                    }

                    // Build is finished but the actual save-to-disk happens on a
                    // user click via ``triggerModelDownloadSave``. Auto-firing the
                    // download here would call ``showSaveFilePicker`` long after the
                    // original click gesture expired, which previously fell through
                    // to an in-memory blob fallback that OOM'd the renderer on
                    // multi-GB Docker tars.
                    if (event.downloadPath && event.filename) {
                        const artifactType = event.artifactType || 'zip';
                        setProgressStatus(prev => {
                            const existingKey = findExistingKey(prev);
                            const targetKey = existingKey || downloadId;
                            const existingItem = prev[targetKey] || {};
                            return {
                                ...prev,
                                [targetKey]: {
                                    ...existingItem,
                                    phase: 'ready_to_save',
                                    buildProgress: 100,
                                    downloadProgress: 0,
                                    progress: 100,
                                    isCompleted: false,
                                    isError: false,
                                    message: artifactType === 'docker'
                                        ? 'Image ready – click "Save to disk"'
                                        : 'Ready – click "Save to disk"',
                                    downloadPath: event.downloadPath,
                                    filename: event.filename,
                                    isDownload: true,
                                    downloadId: targetKey,
                                    usecaseId: event.usecaseId,
                                    version: event.version,
                                    clientId: event.client_id,
                                    artifactType,
                                    sizeBytes: event.sizeBytes ?? existingItem.sizeBytes,
                                },
                            };
                        });
                    }
                } else if (event.isError) {
                    // Update progress to show error
                    setProgressStatus(prev => {
                        const fallbackDownloadId =
                            event.downloadId || `download_${event.usecaseId}_${event.version}`;
                        const existingKey = resolveDownloadProgressKey(
                            prev,
                            event,
                            fallbackDownloadId
                        );

                        const targetKey = existingKey || `download_${event.usecaseId}_${event.version}`;
                        const existingItem = existingKey ? prev[existingKey] : null;

                        return {
                            ...prev,
                            [targetKey]: {
                                ...existingItem, // Preserve existing fields
                                phase: existingItem?.phase || 'building',
                                isError: true,
                                message: event.errorMessage || 'Model export failed',
                                isDownload: true,
                                downloadId: targetKey,
                                usecaseId: event.usecaseId,
                                version: event.version,
                                clientId: event.client_id,
                                // Keep the original datasetName from the existing item
                                datasetName: existingItem?.datasetName || `Model v${event.version}`,
                                artifactType: event.artifactType || existingItem?.artifactType || 'zip',
                            }
                        };
                    });
                    sparkToast.error(`Model export failed: ${event.errorMessage || 'Unknown error'}`);
                } else if (event.progress !== null && event.progress !== undefined) {
                    // Intermediate progress updates emitted by deployment_handler while
                    // create_for_download climbs through 15/35/60/75/95. Clamp to be
                    // monotonic so a late or duplicate event (e.g. 35 arriving after
                    // 75 due to WS reconnect or out-of-order delivery) cannot pull
                    // the bar backwards.
                    setProgressStatus(prev => {
                        const fallbackDownloadId =
                            event.downloadId || `download_${event.usecaseId}_${event.version}`;
                        const existingKey = resolveDownloadProgressKey(
                            prev,
                            event,
                            fallbackDownloadId
                        );

                        const existingItem = existingKey ? prev[existingKey] : null;
                        const targetKey = existingKey || `download_${event.usecaseId}_${event.version}`;

                        const incomingProgress = typeof event.progress === 'number'
                            ? event.progress
                            : 0;
                        const previousProgress = typeof existingItem?.progress === 'number'
                            ? existingItem.progress
                            : 0;
                        const nextProgress = Math.max(incomingProgress, previousProgress);
                        const artifactType =
                            event.artifactType || existingItem?.artifactType || 'zip';

                        if (
                            artifactType === 'ecr' ||
                            artifactType === 'gcr' ||
                            artifactType === 'dockerhub'
                        ) {
                            const ecrPhased = mapEcrOverallProgress(nextProgress);
                            return {
                                ...prev,
                                [targetKey]: {
                                    ...existingItem,
                                    ...ecrPhased,
                                    progress: nextProgress,
                                    isCompleted: false,
                                    message: event.message || `Preparing model... ${nextProgress}%`,
                                    isDownload: true,
                                    downloadId: targetKey,
                                    usecaseId: event.usecaseId,
                                    version: event.version,
                                    clientId: event.client_id,
                                    datasetName: existingItem?.datasetName || `Model v${event.version}`,
                                    artifactType,
                                    sizeBytes: event.sizeBytes ?? existingItem?.sizeBytes,
                                },
                            };
                        }

                        const previousBuild = typeof existingItem?.buildProgress === 'number'
                            ? existingItem.buildProgress
                            : 0;
                        const buildProgress = Math.max(nextProgress, previousBuild);

                        return {
                            ...prev,
                            [targetKey]: {
                                ...existingItem,
                                phase: 'building',
                                buildProgress,
                                downloadProgress: existingItem?.downloadProgress ?? 0,
                                progress: buildProgress,
                                isCompleted: false,
                                message: event.message || `Preparing model... ${buildProgress}%`,
                                isDownload: true,
                                downloadId: targetKey,
                                usecaseId: event.usecaseId,
                                version: event.version,
                                clientId: event.client_id,
                                datasetName: existingItem?.datasetName || `Model v${event.version}`,
                                artifactType,
                                sizeBytes: event.sizeBytes ?? existingItem?.sizeBytes,
                            }
                        };
                    });
                }
                break;
            case "updateGenerationProgress":
                {
                    const genKey = aiGenerationProgressKey(event.usecaseId, event.datasetName);
                    const cancelledAt = cancelledGenerationKeys.current.get(genKey);
                    if (cancelledAt && (Date.now() - cancelledAt < 60_000)) {
                        break;
                    }
                    if (cancelledAt) {
                        cancelledGenerationKeys.current.delete(genKey);
                    }
                    const isCancelled = event.status === 'cancelled';
                    if (isCancelled) {
                        removeProgressItem(genKey);
                        if (event.usecaseId) {
                            setGenerationProgress((prev) => {
                                const next = { ...prev };
                                const current = prev[event.usecaseId];
                                if (current?.datasetName && event.datasetName && current.datasetName !== event.datasetName) {
                                    return prev;
                                }
                                delete next[event.usecaseId];
                                return next;
                            });
                        }
                        if (event.usecaseId) {
                            setAllUsecases((prev) => prev.map((usecase) => {
                                if (usecase._id !== event.usecaseId) return usecase;
                                const nextPending = applyImproveRetrainGenerationTerminal(
                                    usecase.improve_retrain_pending,
                                    {
                                        datasetName: event.datasetName,
                                        status: event.status,
                                    },
                                );
                                const nextAutoTrain = applyAutoTrainFlowTerminal(
                                    usecase.auto_train_pending,
                                    {
                                        flowType: 'generation',
                                        datasetName: event.datasetName,
                                        status: event.status,
                                        error: event.errorDetails,
                                    },
                                );
                                if (
                                    nextPending === usecase.improve_retrain_pending &&
                                    nextAutoTrain === usecase.auto_train_pending
                                ) return usecase;
                                return {
                                    ...usecase,
                                    improve_retrain_pending: nextPending,
                                    auto_train_pending: nextAutoTrain,
                                };
                            }));
                            evaluateAutoTrainTerminalRef.current?.(event.usecaseId, {
                                flowType: 'generation',
                                datasetName: event.datasetName,
                                status: event.status,
                                error: event.errorDetails,
                            });
                        }
                        break;
                    }
                }
                setGenerationProgress(prev => ({
                    ...prev,
                    [event.usecaseId]: {
                        ...(prev[event.usecaseId] || {}),
                        usecaseId: event.usecaseId,
                        datasetName: event.datasetName,
                        progress: event.progress,
                        status: event.status,
                        phase: event.phase,
                        totalPrompts: event.totalPrompts,
                        processedPrompts: event.processedPrompts,
                        errorDetails: event.errorDetails,
                    },
                }));

                {
                    const isFailed = event.status === 'failed';
                    const isCompleted = event.status === 'completed';
                    const pct = inProgressStepPercent(event.progress || 0);

                    // Partial success: the run completed but some examples were
                    // skipped (blocked by the content filter, rate limited, or
                    // another provider error). Surface this as a non-blocking
                    // warning rather than a hard error so the successfully
                    // labeled rows are still treated as done.
                    const failedPrompts = event.failedPrompts || 0;
                    const isWarning = isCompleted && failedPrompts > 0;
                    const totalPrompts = event.totalPrompts || 0;
                    // Agentic TRAJECTORY / summary fields ride the same payload
                    // as top-level snake_case keys (absent for LABEL_ONLY runs
                    // and older backends).
                    const generationSummary = normalizeGenerationProgressPayload(event);
                    const isTrajectory = isTrajectoryGenerationType(generationSummary.generationType);
                    const summaryExtra = generationSummaryExtra(generationSummary);
                    const warningMessage = isWarning
                        ? buildLabelGenWarningMessage({
                            failedPrompts,
                            totalPrompts,
                            failureBreakdown: event.failureBreakdown,
                            failureReason: event.failureReason,
                        })
                        : undefined;
                    // Completed run that skipped rows (trajectory without tool
                    // calls, rejected generated examples, ...): the backend
                    // `summary` is the warning body, `skipped_reasons` the details.
                    const summaryWarningFields = isCompleted && !isWarning
                        ? buildGenerationSummaryWarningFields(generationSummary)
                        : null;
                    const warningFields = isWarning
                        ? {
                            isWarning: true,
                            warningMessage,
                            unlabeledCount: failedPrompts,
                            totalExamples: totalPrompts,
                            failureBreakdown: event.failureBreakdown,
                            // Reason -> first raw error message, for the warning
                            // card's "Show details" expander.
                            failureDetails: event.failureDetails,
                            ...summaryExtra,
                        }
                        : summaryWarningFields || summaryExtra;

                    setProgressStatus(prev => {
                        const genKey = aiGenerationProgressKey(event.usecaseId, event.datasetName);
                        const foldOptions = {
                            isFailed,
                            isCompleted,
                            pct,
                            errorDetails: event.errorDetails,
                            warningFields,
                            processedPrompts: event.processedPrompts || 0,
                            totalPrompts: event.totalPrompts || 0,
                            phase: event.phase,
                            ...(isTrajectory ? { isTrajectory: true } : {}),
                        };
                        const uploadKey = findLabelGenerationProgressKey(prev, {
                            usecaseId: event.usecaseId,
                            datasetName: event.datasetName,
                            excludeKey: genKey,
                        });
                        if (uploadKey) {
                            return {
                                ...prev,
                                [uploadKey]: foldGenerationProgressIntoUploadItem(prev[uploadKey], foldOptions),
                            };
                        }
                        // Retrieval Agent end-of-ingest chaining: show the
                        // trajectory step on the agent's card instead of a
                        // standalone Generate-with-AI card.
                        if (isTrajectory) {
                            const retrievalKey = findRetrievalAgentProgressKeyForDataset(prev, event.datasetName);
                            if (retrievalKey) {
                                return {
                                    ...prev,
                                    [retrievalKey]: foldGenerationProgressIntoUploadItem(
                                        markRetrievalAgentTrajectoryGeneration(prev[retrievalKey]),
                                        foldOptions,
                                    ),
                                };
                            }
                        }

                        const legacyKey = aiGenerationProgressKey(event.usecaseId);
                        const existing = prev[genKey]
                            || (prev[legacyKey]?.isGeneration && prev[legacyKey].datasetName === event.datasetName
                                ? prev[legacyKey]
                                : undefined);
                        return mergeGenerationProgressIntoStatus(
                            prev,
                            applyMonotonicGenerationProgress(
                                existing,
                                generationProgressBarItem({
                                    usecaseId: event.usecaseId,
                                    datasetName: event.datasetName,
                                    status: event.status,
                                    progress: event.progress,
                                    processedPrompts: event.processedPrompts,
                                    totalPrompts: event.totalPrompts,
                                    errorDetails: event.errorDetails,
                                    generationPhase: event.phase,
                                    extra: warningFields,
                                })
                            ),
                            event.usecaseId,
                            event.datasetName,
                        );
                    });

                    if ((isCompleted || isFailed) && event.usecaseId) {
                        setAllUsecases((prev) => prev.map((usecase) => {
                            if (usecase._id !== event.usecaseId) return usecase;
                            const nextPending = applyImproveRetrainGenerationTerminal(
                                usecase.improve_retrain_pending,
                                {
                                    datasetName: event.datasetName,
                                    status: event.status,
                                    failedPrompts,
                                    totalPrompts,
                                    failureReason: event.failureReason,
                                    failureBreakdown: event.failureBreakdown,
                                },
                            );
                            const nextAutoTrain = applyAutoTrainFlowTerminal(
                                usecase.auto_train_pending,
                                {
                                    flowType: 'generation',
                                    datasetName: event.datasetName,
                                    status: event.status,
                                    error: event.errorDetails,
                                },
                            );
                            if (
                                nextPending === usecase.improve_retrain_pending &&
                                nextAutoTrain === usecase.auto_train_pending
                            ) return usecase;
                            return {
                                ...usecase,
                                improve_retrain_pending: nextPending,
                                auto_train_pending: nextAutoTrain,
                            };
                        }));
                        evaluateAutoTrainTerminalRef.current?.(event.usecaseId, {
                            flowType: 'generation',
                            datasetName: event.datasetName,
                            status: event.status,
                            error: event.errorDetails,
                        });
                    }
                }
                break;
            case "updateAgentSessionStatus":
                {
                    setProgressStatus((prev) => {
                        const existing = Object.values(prev || {}).find(
                            (entry) =>
                                entry?.isRetrievalAgent &&
                                ((event.sessionId && entry.sessionId === event.sessionId) ||
                                    (event.usecaseId &&
                                        entry.usecaseId === event.usecaseId &&
                                        event.datasetName &&
                                        entry.datasetName === event.datasetName))
                        );
                        const item = retrievalAgentBarItem({
                            usecaseId: event.usecaseId || existing?.usecaseId,
                            sessionId: event.sessionId || existing?.sessionId,
                            datasetName: event.datasetName || existing?.datasetName,
                            status: event.status,
                            message: event.message,
                            isError: event.isError,
                            errorDetails: event.errorDetails,
                        });
                        return mergeRetrievalAgentProgressIntoStatus(prev, item);
                    });
                    if (event.isError) {
                        sparkToast.error(
                            event.errorDetails || event.message || "Retrieval Agent failed"
                        );
                        fetchUsecasesRef.current?.();
                    } else if (event.isCompleted) {
                        sparkToast.success(event.message || "Retrieval Agent completed");
                        fetchUsecasesRef.current?.();
                    }
                    if ((event.isError || event.isCompleted) && event.usecaseId) {
                        const agentFlow = {
                            flowType: 'retrieval',
                            datasetName: event.datasetName,
                            sessionId: event.sessionId,
                            status: event.status,
                            error: event.errorDetails || event.message,
                        };
                        setAllUsecases((prev) => prev.map((usecase) => {
                            if (usecase._id !== event.usecaseId) return usecase;
                            const nextAutoTrain = applyAutoTrainFlowTerminal(
                                usecase.auto_train_pending,
                                agentFlow,
                            );
                            if (nextAutoTrain === usecase.auto_train_pending) return usecase;
                            return { ...usecase, auto_train_pending: nextAutoTrain };
                        }));
                        evaluateAutoTrainTerminalRef.current?.(event.usecaseId, agentFlow);
                    }
                }
                break;
            case "updateAutoTrainStatus": {
                // Terminal outcome of a scheduled auto-train (backend_consumer's
                // settle thread): started_training / no_data / train_failed /
                // all_failed. The backend already cleared auto_train_pending —
                // mirror that locally so the docked card / Retrain banner stop
                // showing a stale "Starting", toast the outcome, and refetch.
                const localUsecase = allUsecasesRef.current?.find(
                    (usecase) => usecase._id === event.usecaseId
                );
                const resolution = resolveAutoTrainStatusUpdate(event, {
                    hasLocalPending: Boolean(localUsecase?.auto_train_pending),
                });
                if (!resolution) break;
                updateUsecase(event.usecaseId, resolution.usecasePatch);
                if (resolution.toast) {
                    if (resolution.toast.type === 'success') sparkToast.success(resolution.toast.text);
                    else if (resolution.toast.type === 'info') sparkToast.info(resolution.toast.text);
                    else sparkToast.error(resolution.toast.text);
                }
                fetchUsecasesRef.current?.();
                break;
            }
            default:
                console.error("Unknown event:", event);
                break;
        }
    }, [updateStatus, updateUsecase, currentUsecaseId, unregisterUploadGroup, removeProgressItem]);


    const getCurrentUsecase = useCallback(() => {
        return allUsecases.find(usecase => usecase._id === currentUsecaseId) || null;
    }, [allUsecases, currentUsecaseId]);

    const seedGenerationProgress = useCallback(({ usecaseId, datasetName, status = 'queued', jobsAhead } = {}) => {
        const item = seedGenerationProgressItem({ usecaseId, datasetName, status, jobsAhead });
        if (!item) return;
        setProgressStatus((prev) => mergeGenerationProgressIntoStatus(prev, item, usecaseId));
    }, []);

    const seedRetrievalAgentProgress = useCallback(
        ({ usecaseId, sessionId, datasetName, status = 'queued', jobsAhead } = {}) => {
            const item = seedRetrievalAgentProgressItem({ usecaseId, sessionId, datasetName, status, jobsAhead });
            if (!item) return;
            setProgressStatus((prev) => mergeRetrievalAgentProgressIntoStatus(prev, item));
        },
        []
    );

    // Latest progressStatus for interval callbacks (queued-position polling)
    // without re-running the effects on every progress update.
    const progressStatusRef = useRef({});
    useEffect(() => {
        progressStatusRef.current = progressStatus;
    }, [progressStatus]);

    // Auto-train after data flows: react to a terminal generation/agent event
    // on an armed usecase. backend_consumer makes the authoritative decision
    // (record the flow, then train / disarm); this mirrors it locally so the
    // docked card and toasts update immediately, and schedules a refetch so
    // the server outcome reconciles the doc.
    const evaluateAutoTrainAfterTerminal = useCallback((usecaseId, terminalFlow) => {
        if (!usecaseId || !terminalFlow) return;
        const usecase = allUsecasesRef.current.find((entry) => entry._id === usecaseId);
        const pending = usecase?.auto_train_pending;
        if (!pending) return;

        // An AI-labeled upload whose ingest completed keeps flowing locally as
        // label generation — that generation job's terminal event re-evaluates,
        // so counting the upload as "last flow finished" here would be
        // premature (the backend keeps waiting on the LABEL_ONLY job).
        if (terminalFlow.flowType === 'upload' && terminalFlow.status === 'completed') {
            const entries = progressStatusRef.current || {};
            const entry = entries[terminalFlow.datasetName]
                || Object.values(entries).find(
                    (item) => item?.datasetName === terminalFlow.datasetName && !item?.isGeneration
                );
            if (entry?.isLabelGeneration && !entry?.isError) return;
        }

        setTimeout(() => fetchUsecasesRef.current?.(), 2500);

        const mirrored = applyAutoTrainFlowTerminal(pending, terminalFlow);
        const remaining = listActiveDataFlowsForUsecase(progressStatusRef.current, usecaseId)
            .filter((flow) => {
                // progressStatusRef may not have absorbed this event yet, so
                // exclude the flow that just finished from the remaining set.
                if (terminalFlow.sessionId && flow.sessionId === terminalFlow.sessionId) {
                    return false;
                }
                if (
                    terminalFlow.flowType === 'generation' &&
                    flow.flowType === 'generation' &&
                    flow.datasetName === terminalFlow.datasetName
                ) {
                    return false;
                }
                // Uploads: single-objective entries are keyed by the event's
                // dataset name. Coalesced multi-objective groups deliberately
                // do NOT match (their parent entry stays active until every
                // child clone finishes).
                if (
                    terminalFlow.flowType === 'upload' &&
                    flow.flowType === 'upload' &&
                    (flow.datasetName === terminalFlow.datasetName ||
                        flow.progressKey === terminalFlow.datasetName)
                ) {
                    return false;
                }
                return true;
            });
        if (remaining.length > 0) return;

        if (hasSucceededAutoTrainFlow(mirrored)) {
            sparkToast.success('All data flows finished — training will start automatically');
            updateUsecase(usecaseId, {
                auto_train_pending: { ...mirrored, triggering: true },
            });
            return;
        }
        sparkToast.info('Automatic training canceled — no data flow completed successfully');
        updateUsecase(usecaseId, { auto_train_pending: null });
    }, [updateUsecase]);
    useEffect(() => {
        evaluateAutoTrainTerminalRef.current = evaluateAutoTrainAfterTerminal;
    }, [evaluateAutoTrainAfterTerminal]);

    // Restore the floating generation bar after refresh. GET already existed
    // for the continue banner; progressStatus was in-memory only. While a
    // generation job is still queued, keep polling so its queue position
    // (jobs ahead) stays fresh.
    useEffect(() => {
        if (!currentUsecaseId) return undefined;
        let cancelled = false;
        const fetchStatus = () => {
            data_from_server_get(`ai-generation/status/${currentUsecaseId}`, (result) => {
                if (cancelled) return;
                const items = generationProgressItemsFromStatus(result, currentUsecaseId);
                if (!items.length) return;
                setProgressStatus((prev) => {
                    let next = prev;
                    items.forEach((item) => {
                        const genKey = aiGenerationProgressKey(currentUsecaseId, item.datasetName);
                        const cancelledAt = cancelledGenerationKeys.current.get(genKey);
                        if (cancelledAt && (Date.now() - cancelledAt < 60_000)) return;
                        next = mergeGenerationProgressIntoStatus(next, item, currentUsecaseId, item.datasetName);
                    });
                    return next;
                });
            });
        };
        fetchStatus();
        const interval = setInterval(() => {
            const hasQueued = Object.values(progressStatusRef.current || {}).some(
                (entry) =>
                    entry?.isGeneration
                    && entry.isQueued
                    && !entry.isError
                    && !entry.isCompleted
                    && entry.usecaseId === currentUsecaseId
            );
            if (hasQueued) fetchStatus();
        }, QUEUED_STATUS_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [currentUsecaseId]);

    // Same for the Retrieval Agent: restore on load, poll while queued.
    useEffect(() => {
        if (!currentUsecaseId) return undefined;
        let cancelled = false;
        const fetchStatus = () => {
            data_from_server_get(`retrieval-agent/status/${currentUsecaseId}`, (result) => {
                if (cancelled) return;
                const items = retrievalAgentItemsFromStatus(result, currentUsecaseId);
                if (!items.length) return;
                setProgressStatus((prev) => {
                    let next = prev;
                    items.forEach((item) => {
                        next = mergeRetrievalAgentProgressIntoStatus(next, item);
                    });
                    return next;
                });
            });
        };
        fetchStatus();
        const interval = setInterval(() => {
            const hasQueued = Object.values(progressStatusRef.current || {}).some(
                (entry) =>
                    entry?.isRetrievalAgent
                    && entry.status === 'queued'
                    && !entry.isError
                    && !entry.isCompleted
                    && entry.usecaseId === currentUsecaseId
            );
            if (hasQueued) fetchStatus();
        }, QUEUED_STATUS_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [currentUsecaseId]);

    // Update currentUsecase when currentUsecaseId or allUsecases change
    useEffect(() => {
        const foundUsecase = allUsecases.find(usecase => usecase._id === currentUsecaseId) || null;
        setCurrentUsecase(foundUsecase);
    }, [allUsecases, currentUsecaseId]);


    const updatePromptAndTeacher = useCallback((prompt, teacher_model, comparison_mode, teacher_provider, teacher_model_display) => {
        if (!currentUsecaseId) return;
        const update = { prompt, teacher_model };
        if (comparison_mode !== undefined) update.comparison_mode = comparison_mode;
        if (teacher_provider !== undefined) update.teacher_provider = teacher_provider;
        if (teacher_model_display !== undefined) update.teacher_model_display = teacher_model_display;
        updateUsecase(currentUsecaseId, update);
    }, [currentUsecaseId, updateUsecase]);

    const updateParserFunction = useCallback((parser_function) => {
        if (!currentUsecaseId) return;
        updateUsecase(currentUsecaseId, {
            parser_function,
            parser_function_validation_failed: false,
            parser_function_validation_message: null,
        });
    }, [currentUsecaseId, updateUsecase]);

    const updateParserValidationStatus = useCallback((failed, message = null) => {
        if (!currentUsecaseId) return;
        updateUsecase(currentUsecaseId, {
            parser_function_validation_failed: failed,
            parser_function_validation_message: message,
        });
    }, [currentUsecaseId, updateUsecase]);

    const updateVersion = useCallback((usecaseId, newVersion) => {
        updateUsecase(usecaseId, { version: newVersion });
    }, [updateUsecase]);

    const fetchUsecases = useCallback(async (successCallback) => {
        try {
            setIsFetchingUsecases(true);
            data_from_server_post(
                "llm_usecases",
                { "value": "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75" },
                (apps) => {
                    const allUsecases = (apps || []).flatMap(app =>
                        (app?.sub_groups || []).flatMap(sub_group =>
                            (sub_group?.usecases || []).map(usecase => ({
                                ...usecase,
                                group_name: app.group_name,
                                // Bug fix: previously read app.sub_group_name (always
                                // undefined). Tasks rely on this field being populated
                                // from the parent sub_group.
                                sub_group_name: usecase.sub_group_name ?? sub_group.sub_group_name ?? '',
                                project_goal: app.project_goal
                            }))
                        )
                    ).sort((a, b) => {
                        // First sort by group_name
                        const groupComparison = (a.group_name || '').localeCompare(b.group_name || '');
                        if (groupComparison !== 0) {
                            return groupComparison;
                        }
                        // If group names are the same, sort by usecase_name
                        return (a.usecase_name || '').localeCompare(b.usecase_name || '');
                    });
                    setAllUsecases(allUsecases);
                    setIsFetchingUsecases(false);
                    if (successCallback) {
                        successCallback(allUsecases);
                    }
                }
            );
        } catch (error) {
            sparkToast.error("Failed to fetch usecases");
            setIsFetchingUsecases(false);
        }
    }, []);

    fetchUsecasesRef.current = fetchUsecases;

    useEffect(() => {
        // Only fetch if we don't already have usecases or if we're explicitly refreshing
        if (allUsecases.length === 0) {
            fetchUsecases();
        }
    }, [fetchUsecases]);

    // ---------------------------------------------------------------
    // Commercial model catalog (``GET /available-models``)
    //
    // Backed by the process-wide ``ModelManager`` singleton on the backend,
    // which is itself an idempotent mirror of ``OrganizationSettings``.
    //
    // ``availableProviders`` is the single source of truth for the catalog:
    // a direct pass-through of the backend's ``providers`` payload, keyed by
    // provider name:
    //
    //   {
    //     <provider_name>: {
    //       provider_type,                  // "vendor" | "cloud"
    //       models: [{ id, vendor }, ...],  // ModelInfo entries
    //     }
    //   }
    //
    // ``provider_type`` comes from the backend ``ProviderConfig`` so the
    // client never re-derives the type from the name. ``vendor`` on each
    // model is the company that produced the model (the provider's own
    // name for vendor providers; the underlying model maker for cloud
    // providers — Bedrock surfaces ``"Anthropic"`` / ``"Meta"`` /
    // ``"Amazon"`` etc.). Consumers derive vendor / cloud lists, per-
    // provider model arrays, etc. from this single map.
    //
    // We hold the catalog here so model selectors don't refetch on every
    // render. Refetch happens in two scenarios:
    //   1. App load (the effect below).
    //   2. The user saves new keys / cloud credentials in Account Settings —
    //      Settings.jsx calls ``refreshCommercialModels`` from this context.
    //
    // To avoid the "Vendor segment flicker" that ``ModelSelection`` shows
    // while ``GET /available-models`` is in flight on a cold load, we
    // hydrate the initial state from ``localStorage`` synchronously and
    // re-write the cache on every successful fetch. Subsequent app loads
    // get correct data on first paint while the background refresh
    // reconciles silently.
    // ---------------------------------------------------------------
    const [availableProviders, setAvailableProviders] = useState(
        () => readCachedProviders()
    );
    // ``isCommercialModelsLoading`` lets callers distinguish "still fetching"
    // from "loaded but no providers configured" — the latter is also
    // represented by ``availableProviders`` being an empty object. Callers
    // use this to avoid flashing the "Provider credentials required" banner
    // on first paint. When we hydrate from cache, we start with
    // ``isLoading = false`` so callers immediately render their normal
    // (non-skeleton) state; the background refresh updates state in place
    // if anything changed.
    const [isCommercialModelsLoading, setIsCommercialModelsLoading] = useState(
        () => Object.keys(readCachedProviders()).length === 0
    );

    const refreshCommercialModels = useCallback(() => {
        setIsCommercialModelsLoading(true);
        data_from_server_get('available-models', (response) => {
            if (!response || response.error) {
                setAvailableProviders({});
                writeCachedProviders({});
            } else {
                const providers = response.providers || {};
                setAvailableProviders(providers);
                writeCachedProviders(providers);
            }
            setIsCommercialModelsLoading(false);
        });
    }, []);

    useEffect(() => {
        refreshCommercialModels();
    }, [refreshCommercialModels]);

    return (
        <GlobalContext.Provider value={{
            // Current Usecase states and functions
            currentUsecaseId,
            setCurrentUsecaseId,
            currentUsecase,
            setCurrentUsecase,
            getCurrentUsecase,
            updateUsecase,
            updatePromptAndTeacher,
            updateParserFunction,
            updateParserValidationStatus,
            updateStatus,
            updateVersion,
            viewType,
            setViewTypeState,

            // LLM Usecases states and functions
            refreshUsecases: fetchUsecases,
            allApps,
            currentSubGroup,
            currentSubGroupKey,
            allUsecases,
            isFetchingUsecases,
            setAllUsecases,
            activityLog,
            trainStatuses,
            markTrainingStatusCanceled,
            registerStartedTraining,
            syncFinalTrainStatuses,
            realtimeResyncTick,
            requestRealtimeResync,
            lastMessages,
            handleWsMessage,
            setActivityLog,
            progressStatus,
            setProgressStatus,
            seedGenerationProgress,
            seedRetrievalAgentProgress,
            removeProgressItem,
            registerUploadAbort,
            unregisterUploadAbort,
            registerUploadGroup,
            unregisterUploadGroup,
            cancelUpload,
            cancelGeneration,
            triggerModelDownloadSave,
            clearCancelledUploadSuppression,
            generationProgress,
            setGenerationProgress,
            evaluationProgress,
            setEvaluationProgress,
            modelPerfRunStatus,
            evaluationResultsReady,
            cleanupCompleteSignal,
            uploadCancelInitiatedSignal,
            appendCancelPreserveAnalysisRef,

            // Commercial model catalog (see refreshCommercialModels above)
            availableProviders,
            isCommercialModelsLoading,
            refreshCommercialModels,
        }}>
            {children}
        </GlobalContext.Provider>
    );
};
